import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

export interface UseUrlStepThroughConfig<TStep extends string> {
  /** The URL query parameter key to store the step */
  paramKey: string;
  /** Ordered array of steps (first element is the default/initial step) */
  steps: readonly TStep[];
}

export interface UseUrlStepThroughReturn<TStep extends string> {
  /** The current step from URL (or first step in array) */
  currentStep: TStep;
  /** Index of the current step in the steps array */
  currentStepIndex: number;
  /** Whether we're on the first step */
  isFirstStep: boolean;
  /** Whether we're on the last step */
  isLastStep: boolean;
  /** Navigate to a specific step */
  goToStep: (step: TStep) => void;
  /** Navigate to the next step */
  goToNextStep: () => void;
  /** Navigate to the previous step */
  goToPreviousStep: () => void;
  /** Remove the step param from the URL */
  clearStep: () => void;
}

/**
 * Generic hook to manage step navigation using URL search params.
 * Useful for multi-step modals, wizards, or any flow that needs URL-persisted state.
 *
 * @example
 * ```tsx
 * const STEPS = ['details', 'review', 'confirm'] as const;
 *
 * const {
 *   currentStep,
 *   isFirstStep,
 *   isLastStep,
 *   goToNextStep,
 *   goToPreviousStep,
 *   clearStep,
 * } = useUrlStepThrough({
 *   paramKey: 'wizardStep',
 *   steps: STEPS,
 * });
 * ```
 */
export const useUrlStepThrough = <TStep extends string>(
  config: UseUrlStepThroughConfig<TStep>
): UseUrlStepThroughReturn<TStep> => {
  const { paramKey, steps } = config;
  const router = useRouter();

  const defaultStep = steps[0];
  const currentStep = (router.query[paramKey] as TStep) || defaultStep;

  const currentStepIndex = useMemo(() => {
    const index = steps.indexOf(currentStep);
    return index >= 0 ? index : 0;
  }, [steps, currentStep]);

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const goToStep = useCallback(
    (step: TStep) => {
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, [paramKey]: step },
        },
        undefined,
        { shallow: true }
      );
    },
    [router, paramKey]
  );

  const goToNextStep = useCallback(() => {
    if (!isLastStep) {
      goToStep(steps[currentStepIndex + 1]);
    }
  }, [currentStepIndex, isLastStep, goToStep, steps]);

  const goToPreviousStep = useCallback(() => {
    if (!isFirstStep) {
      goToStep(steps[currentStepIndex - 1]);
    }
  }, [currentStepIndex, isFirstStep, goToStep, steps]);

  const clearStep = useCallback(() => {
    const restQuery = { ...router.query };
    delete restQuery[paramKey];
    router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
  }, [router, paramKey]);

  return {
    currentStep,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    clearStep,
  };
};
